// Aignoz Innovation OS — Phase 3 Discovery layer.
// Pure frontend. No new LLM calls. Deterministic mock data + derivations from existing runs.
import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Atom, ArrowRight, Sparkles, Layers, Cpu, Brain, Boxes, TrendingUp, Dna,
  ChevronDown, ChevronUp, ScanSearch, Map as MapIcon, Target as TargetIcon,
  Wrench, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInsights } from "@/shell";
import { formatUSD } from "@/lib";

const AMR_ID = "ee0c66ab-8d48-4614-9054-dd33b09c160d";
const SEPSIS_ID = "fc526bce-d199-433b-8475-8ba28829527f";

// ─── Axis definitions ───────────────────────────────────────────────────────
const BIOLOGY = ["Immune System","Nervous System","Cardiovascular","Respiratory","Reproductive","Endocrine","Musculoskeletal","Microbiome","Aging Biology","Developmental Biology"];
const TECH = ["AI","Agentic AI","Digital Twins","Genomics","Transcriptomics","Proteomics","Glycomics","CRISPR","Synthetic Biology","Microfluidics","Nanotechnology","Robotics","Spatial Biology","Quantum Computing","Advanced Sensors"];
const FUNC = ["Prevention","Screening","Diagnosis","Risk Prediction","Monitoring","Treatment","Drug Discovery","Rehabilitation","Public Health","Precision Medicine"];

// Deterministic hash for mock scoring
function hash(s) { let h = 0; for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; }
function cellScore(bio, tech, func) {
  let s = 40 + (hash(bio + "|" + tech + "|" + func) % 50);
  // Hand-tuned high-signal cells (storytelling)
  const map = {
    "Microbiome|AI|Diagnosis": 96,                              // AMR
    "Microbiome|Advanced Sensors|Diagnosis": 92,
    "Immune System|Advanced Sensors|Risk Prediction": 94,       // Sepsis
    "Immune System|AI|Diagnosis": 89,
    "Nervous System|Digital Twins|Precision Medicine": 88,
    "Aging Biology|Proteomics|Risk Prediction": 90,
    "Cardiovascular|Digital Twins|Treatment": 86,
    "Reproductive|Proteomics|Screening": 87,
    "Endocrine|Agentic AI|Monitoring": 85,
    "Microbiome|Synthetic Biology|Treatment": 88,
    "Aging Biology|Spatial Biology|Drug Discovery": 86,
    "Nervous System|AI|Rehabilitation": 84,
  };
  const k = `${bio}|${tech}|${func}`;
  return map[k] ?? s;
}
function scoreColor(s) {
  if (s >= 85) return { bg: "rgba(94,234,212,0.32)", border: "rgba(94,234,212,0.65)", text: "#5EEAD4", glow: true };
  if (s >= 75) return { bg: "rgba(251,146,60,0.22)", border: "rgba(251,146,60,0.55)", text: "#FB923C" };
  if (s >= 60) return { bg: "rgba(251,191,36,0.16)", border: "rgba(251,191,36,0.45)", text: "#FBBF24" };
  return { bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.08)", text: "rgba(255,255,255,0.4)" };
}
// Linked ventures for AMR + Sepsis cells
const LINKED = {
  "Microbiome|AI|Diagnosis": { id: AMR_ID, company: "ZoneSight Diagnostics", label: "AMR" },
  "Immune System|Advanced Sensors|Risk Prediction": { id: SEPSIS_ID, company: "FirstMile Sepsis", label: "Sepsis" },
};
const PROFILE_BLURBS = {
  "Nervous System|Digital Twins|Precision Medicine": ["Patient-specific brain network simulation for epilepsy surgery planning","FDA already cleared adjacent precision-mapping tools","Sub-2yr time-to-pivot from research model"],
  "Aging Biology|Proteomics|Risk Prediction": ["Multi-omic frailty index to triage interventions years before onset","Public-cohort biobanks (UK Biobank, India Biobank) lower data cost","Healthspan extension is a $200B+ category in the next decade"],
  "Cardiovascular|Digital Twins|Treatment": ["Personalised LVAD/stent placement via patient-specific haemodynamic twins","Integration with existing imaging stacks accelerates adoption"],
  "Microbiome|Synthetic Biology|Treatment": ["Engineered consortia for refractory C. diff and IBD","Live biotherapeutic regulatory pathway now clarified"],
};

// ═════════════════════════════════════════════════════════════════════════════
// /discover — Innovation Engine landing
// ═════════════════════════════════════════════════════════════════════════════
export function DiscoverHome() {
  const navigate = useNavigate();
  const tiles = [
    { icon: ScanSearch, title: "Innovation Matrix", caption: "1,500-cell intersection map of Biology × Technology × Healthcare Function", to: "/discover/matrix" },
    { icon: MapIcon, title: "Opportunity Heatmap", caption: "Ten Billion-Dollar Problem Detectors scoring healthcare whitespace", to: "/discover/heatmap" },
    { icon: Layers, title: "5-Layer Venture Architecture", caption: "Every venture engineered across Biology → Technology → Intelligence → Platform → Business", to: `/projects/${AMR_ID}/overview` },
  ];
  const steps = [
    { label: "SCAN",      sub: "Disease burden × tech readiness signals across global cohorts" },
    { label: "MAP",       sub: "Plot opportunities on the 3-axis Innovation Matrix" },
    { label: "SCORE",     sub: "10 Billion-Dollar Problem Detectors rank candidates" },
    { label: "ENGINEER",  sub: "12-agent pipeline builds venture-ready company across 5 layers" },
  ];
  return (
    <div className="px-10 py-8 max-w-[1280px] mx-auto" data-testid="discover-home">
      <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/80">Aignoz DeepTech Innovation Engine™</div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Systematic Healthcare Opportunity Discovery</h1>
      <p className="text-base italic text-white/65 mt-3 max-w-3xl leading-relaxed">
        We don't merely generate ventures. We systematically discover high-value healthcare opportunities across biology, technology, and healthcare function — then engineer them into venture-ready companies.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        {tiles.map((t, i) => (
          <motion.button key={t.title} onClick={() => navigate(t.to)}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`discover-tile-${i}`}
            className="text-left glass rounded-xl border-white/[0.06] p-6 hover:border-teal-400/30 transition group">
            <t.icon className="w-7 h-7 text-teal-300/80 mb-4" />
            <div className="text-xl font-semibold tracking-tight">{t.title}</div>
            <p className="text-sm text-white/65 mt-2 leading-relaxed">{t.caption}</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-teal-300/80 group-hover:text-teal-200">
              Open <ArrowRight className="w-3 h-3" />
            </div>
          </motion.button>
        ))}
      </div>

      <div className="mt-10">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-3">Methodology</div>
        <div className="grid grid-cols-4 gap-3">
          {steps.map((s, i) => (
            <div key={s.label} className="relative rounded-lg border border-white/[0.06] bg-white/[0.015] p-4">
              <div className="text-[10px] mono text-teal-300/60 mb-1">0{i + 1}</div>
              <div className="text-lg font-semibold tracking-tight text-teal-100">{s.label}</div>
              <div className="text-xs text-white/55 mt-1 leading-relaxed">{s.sub}</div>
              {i < 3 && (
                <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-400/50" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// /discover/matrix — Innovation Matrix (Biology selector + Tech × Function grid)
// ═════════════════════════════════════════════════════════════════════════════
export function InnovationMatrix() {
  const navigate = useNavigate();
  const [bio, setBio] = useState("Immune System");
  const [selected, setSelected] = useState(null);

  useInsights(
    selected ? <CellInsights cell={selected} onOpen={(id) => navigate(`/projects/${id}/overview`)} /> : <MatrixDefaultInsights />,
    [selected]
  );

  return (
    <div className="px-8 py-6 max-w-[1320px] mx-auto" data-testid="innovation-matrix">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/80">Discovery</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Innovation Matrix</h1>
          <p className="text-sm text-white/60 mt-1">1,500-cell intersection map. Select a biological system to filter the Technology × Healthcare Function grid.</p>
        </div>
      </div>

      {/* Biology selector */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="bio-selector">
        {BIOLOGY.map((b) => (
          <button key={b} onClick={() => { setBio(b); setSelected(null); }}
            data-testid={`bio-${b.toLowerCase().replace(/\s+/g, "-")}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
              b === bio
                ? "bg-teal-400/15 border-teal-400/50 text-teal-100 shadow-[0_0_18px_rgba(94,234,212,0.25)]"
                : "border-white/[0.08] text-white/60 hover:border-white/[0.18] hover:text-white"
            }`}>
            {b}
          </button>
        ))}
      </div>

      {/* Matrix grid: rows = TECH (15), cols = FUNC (10) */}
      <div className="glass rounded-xl border-white/[0.06] p-3 overflow-auto">
        <table className="border-collapse text-[10px]" style={{ minWidth: 980 }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-[#0F1116] p-1 text-left text-white/40 uppercase tracking-wider w-[140px]">Tech \ Function</th>
              {FUNC.map((f) => (
                <th key={f} className="p-1 text-left text-white/55 font-normal align-bottom" style={{ minWidth: 78 }}>
                  <div className="leading-tight">{f}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TECH.map((t) => (
              <tr key={t}>
                <td className="sticky left-0 z-10 bg-[#0F1116] p-1 pr-2 text-white/70 whitespace-nowrap">{t}</td>
                {FUNC.map((f) => {
                  const s = cellScore(bio, t, f);
                  const c = scoreColor(s);
                  const key = `${bio}|${t}|${f}`;
                  const isLinked = !!LINKED[key];
                  const sel = selected?.key === key;
                  return (
                    <td key={f} className="p-0.5">
                      <button
                        title={`${bio} · ${t} · ${f} — ${s}`}
                        data-testid={`cell-${t}-${f}`.toLowerCase().replace(/\s+/g, "-")}
                        onClick={() => setSelected({ key, bio, tech: t, func: f, score: s, linked: LINKED[key] })}
                        className={`w-full h-9 rounded-sm border mono font-semibold transition ${c.glow ? "animate-[pulse-dot_2.4s_ease-in-out_infinite]" : ""} ${sel ? "ring-1 ring-teal-300" : ""}`}
                        style={{ background: c.bg, borderColor: c.border, color: c.text }}>
                        {s}
                        {isLinked && <span className="ml-1 text-[8px] align-top">●</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-white/35 mt-3 max-w-3xl">
        Score = composite of disease burden, technology readiness, regulatory pathway clarity, market pull, and patent whitespace. <span className="italic">Demonstration data; production version uses live evidence pipelines.</span>
      </div>
    </div>
  );
}

function CellInsights({ cell, onOpen }) {
  const interp = cell.score >= 85 ? "High opportunity" : cell.score >= 75 ? "Emerging" : cell.score >= 60 ? "Developing" : "Saturated / low signal";
  const c = scoreColor(cell.score);
  const blurbs = PROFILE_BLURBS[cell.key] || ["Whitespace candidate — limited prior art in the literature.","Tech readiness clearing the trough; expect inflection in 18-36 months.","Run this through the Innovation Pipeline to generate a venture brief."];
  return (
    <div className="space-y-4" data-testid="cell-insights">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Selected intersection</div>
        <div className="text-base font-semibold text-teal-100">{cell.bio}</div>
        <div className="text-base font-semibold text-teal-100">× {cell.tech}</div>
        <div className="text-base font-semibold text-teal-100">× {cell.func}</div>
      </div>
      <div className="rounded-md border p-3" style={{ background: c.bg, borderColor: c.border }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-wider" style={{ color: c.text }}>Opportunity</span>
          <span className="mono text-2xl font-semibold" style={{ color: c.text }}>{cell.score}</span>
        </div>
        <div className="text-xs text-white/75">{interp}</div>
      </div>
      {cell.linked ? (
        <div className="rounded-md border border-teal-400/30 bg-teal-400/[0.06] p-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-teal-300/80 mb-1">Linked Venture · {cell.linked.label}</div>
          <div className="text-sm font-semibold mb-2">{cell.linked.company}</div>
          <Button size="sm" onClick={() => onOpen(cell.linked.id)} className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold w-full" data-testid="open-linked-venture">
            Open venture <ExternalLink className="w-3 h-3 ml-1.5" />
          </Button>
        </div>
      ) : (
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/40 mb-2">Opportunity Profile</div>
          <ul className="space-y-2">
            {blurbs.map((b, i) => (
              <li key={i} className="flex gap-2 text-xs text-white/80 leading-relaxed">
                <span className="text-teal-300/70">▸</span>{b}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
function MatrixDefaultInsights() {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">How to read</div>
      <ul className="space-y-2 text-xs text-white/70 leading-relaxed">
        <li className="flex gap-2"><span className="text-teal-300/70">▸</span>Pick a biological system above to filter</li>
        <li className="flex gap-2"><span className="text-teal-300/70">▸</span>Higher cell scores = higher whitespace × tech readiness</li>
        <li className="flex gap-2"><span className="text-teal-300/70">▸</span>● dot = an Aignoz venture already exists at that intersection</li>
        <li className="flex gap-2"><span className="text-teal-300/70">▸</span>Click any cell for the opportunity profile</li>
      </ul>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// /discover/heatmap — Opportunity Heatmap (Problems × 10 Detectors)
// ═════════════════════════════════════════════════════════════════════════════
const DETECTORS = ["Invisible Biology","Delayed Diagnosis","Therapeutic Failure","Biological Heterogeneity","Clinical Workflow Bottleneck","Data Fragmentation","Simulation Deficit","Rare Disease Neglect","Aging","Human-Machine Interfaces"];

// Hand-curated problems with detector scores (0-100)
const PROBLEMS = [
  { id: "amr",          title: "AMR in low-resource hospitals",                    linked: { id: AMR_ID,    label: "ZoneSight Diagnostics" }, scores: [78, 96, 88, 70, 92, 86, 40, 30, 50, 72] },
  { id: "sepsis",       title: "Early sepsis detection",                            linked: { id: SEPSIS_ID, label: "FirstMile Sepsis" },     scores: [82, 95, 80, 74, 90, 78, 55, 25, 60, 70] },
  { id: "ovarian",      title: "Early ovarian cancer detection",                                                                                                          scores: [88, 92, 76, 68, 72, 70, 50, 45, 30, 55] },
  { id: "maternal",     title: "Maternal mortality in low-resource settings",                                                                                              scores: [70, 88, 78, 60, 86, 76, 30, 40, 25, 68] },
  { id: "tb",           title: "Tuberculosis screening at scale",                                                                                                          scores: [60, 90, 74, 58, 84, 80, 35, 30, 35, 64] },
  { id: "alzheimer",    title: "Alzheimer's & dementia early detection",                                                                                                   scores: [90, 86, 78, 84, 72, 74, 78, 35, 95, 60] },
  { id: "multimorbid",  title: "Multimorbidity in aging populations",                                                                                                       scores: [78, 76, 82, 92, 84, 88, 70, 30, 96, 70] },
  { id: "t2d",          title: "Type 2 diabetes prevention",                                                                                                                scores: [62, 70, 76, 70, 72, 82, 65, 20, 70, 78] },
  { id: "peds-rare",    title: "Pediatric rare disease diagnosis",                                                                                                          scores: [80, 90, 65, 78, 72, 80, 60, 96, 30, 60] },
  { id: "stroke-neuro", title: "Stroke recovery / neuro-rehab",                                                                                                             scores: [70, 76, 78, 72, 82, 70, 78, 30, 80, 92] },
  { id: "mental",       title: "Mental health early intervention",                                                                                                          scores: [88, 84, 80, 86, 78, 84, 70, 30, 55, 78] },
  { id: "ibd",          title: "IBD / autoimmune flare prediction",                                                                                                         scores: [82, 80, 78, 88, 70, 80, 76, 40, 45, 62] },
  { id: "cv-young",     title: "Cardiovascular risk in young adults",                                                                                                       scores: [78, 82, 70, 76, 72, 80, 78, 25, 40, 70] },
  { id: "sickle",       title: "Sickle cell disease management (SSA)",                                                                                                      scores: [60, 78, 82, 60, 80, 70, 40, 50, 35, 60] },
];
const PROBLEM_FULL_TEXT = {
  amr: "AI-enabled early detection of antimicrobial resistance in low-resource hospitals",
  sepsis: "Early sepsis diagnosis in rural India",
  ovarian: "Early-stage ovarian cancer screening for high-risk women",
  maternal: "Postpartum hemorrhage detection in low-resource maternity wards",
  tb: "Point-of-care tuberculosis screening in tier-3 cities",
  alzheimer: "Pre-symptomatic Alzheimer's risk stratification in primary care",
  multimorbid: "Continuous risk monitoring across multimorbid aging cohorts",
  t2d: "Behavioural T2D prevention for South Asian phenotypes",
  "peds-rare": "AI-assisted rare disease diagnosis for pediatric outpatients",
  "stroke-neuro": "Home-based neuro-rehabilitation after stroke",
  mental: "Adolescent mental health screening in schools",
  ibd: "IBD flare prediction from at-home biomarkers",
  "cv-young": "Cardiovascular risk in adults under 40",
  sickle: "Continuous sickle cell crisis prevention in sub-Saharan Africa",
};

export function OpportunityHeatmap() {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const sorted = useMemo(
    () => [...PROBLEMS].map((p) => ({ ...p, total: p.scores.reduce((a, b) => a + b, 0) })).sort((a, b) => b.total - a.total),
    []
  );
  const selected = sorted.find((p) => p.id === selectedId);
  useInsights(
    selected ? <ProblemInsights p={selected} onPrefill={(p) => navigate(`/projects/new?prefill=${p.id}`)} onOpen={(id) => navigate(`/projects/${id}/overview`)} /> : <HeatmapDefaultInsights />,
    [selectedId]
  );
  return (
    <div className="px-8 py-6 max-w-[1320px] mx-auto" data-testid="opportunity-heatmap">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/80">Discovery</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Ten Billion-Dollar Problem Detectors</h1>
          <p className="text-sm text-white/60 mt-1 max-w-3xl">We score every healthcare problem across ten dimensions of unmet opportunity. The highest-scoring problems become candidates for venture engineering.</p>
        </div>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-400/10 text-amber-200 border border-amber-400/30">14 seeded problems · demonstration data</span>
      </div>

      <div className="glass rounded-xl border-white/[0.06] p-3 overflow-auto">
        <table className="border-collapse text-[10px] w-full" style={{ minWidth: 980 }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-[#0F1116] p-2 text-left text-white/40 uppercase tracking-wider w-[260px]">Problem</th>
              {DETECTORS.map((d) => (
                <th key={d} className="p-1 text-[9px] text-white/55 font-normal align-bottom" style={{ minWidth: 64 }}>
                  <div className="leading-tight">{d}</div>
                </th>
              ))}
              <th className="p-1 text-[10px] uppercase tracking-wider text-teal-300/80 text-right pr-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id} className={`hover:bg-white/[0.02] ${selectedId === p.id ? "bg-teal-400/[0.04]" : ""}`}>
                <td className="sticky left-0 z-10 bg-[#0F1116] p-2 text-white/85 font-medium align-middle">
                  <button onClick={() => setSelectedId(p.id)} data-testid={`row-${p.id}`} className="text-left w-full">
                    <span className="block truncate">{p.title}</span>
                    {p.linked && <span className="text-[9px] text-teal-300/70">● linked venture</span>}
                  </button>
                </td>
                {p.scores.map((s, i) => {
                  const c = scoreColor(s);
                  return (
                    <td key={i} className="p-0.5">
                      <div className="h-7 rounded-sm border mono text-[10px] flex items-center justify-center font-semibold"
                        style={{ background: c.bg, borderColor: c.border, color: c.text }}>{s}</div>
                    </td>
                  );
                })}
                <td className="p-1 text-right mono text-teal-200 font-semibold pr-2">{p.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProblemInsights({ p, onPrefill, onOpen }) {
  return (
    <div className="space-y-4" data-testid="problem-insights">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">Selected problem</div>
        <div className="text-sm font-semibold text-teal-100 leading-snug">{p.title}</div>
        <div className="text-xs text-white/60 mt-2 leading-relaxed">{PROBLEM_FULL_TEXT[p.id]}</div>
      </div>
      <div className="rounded-md border border-teal-400/30 bg-teal-400/[0.06] p-3 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-teal-300/80">Total opportunity</span>
        <span className="mono text-2xl font-semibold text-teal-100">{p.total}</span>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/40 mb-2">Detector scores</div>
        <div className="space-y-1">
          {DETECTORS.map((d, i) => {
            const s = p.scores[i];
            return (
              <div key={d} className="flex items-center gap-2 text-[11px]">
                <span className="text-white/65 flex-1 truncate">{d}</span>
                <div className="h-1.5 w-20 bg-white/[0.06] rounded overflow-hidden"><div className="h-full bg-teal-400" style={{ width: `${s}%` }} /></div>
                <span className="mono text-teal-200 w-7 text-right">{s}</span>
              </div>
            );
          })}
        </div>
      </div>
      {p.linked && (
        <Button size="sm" onClick={() => onOpen(p.linked.id)} className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold w-full" data-testid="view-existing">
          View existing venture · {p.linked.label} <ExternalLink className="w-3 h-3 ml-1.5" />
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => onPrefill(p)} className="w-full" data-testid="generate-from-problem">
        Generate Venture for this Problem <ArrowRight className="w-3 h-3 ml-1.5" />
      </Button>
    </div>
  );
}
function HeatmapDefaultInsights() {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">How to read</div>
      <ul className="space-y-2 text-xs text-white/70 leading-relaxed">
        <li className="flex gap-2"><span className="text-teal-300/70">▸</span>Rows sorted by total opportunity (sum of 10 detectors)</li>
        <li className="flex gap-2"><span className="text-teal-300/70">▸</span>Click any problem row for the detector breakdown</li>
        <li className="flex gap-2"><span className="text-teal-300/70">▸</span>Top-ranked candidates can be engineered into ventures in one click</li>
      </ul>
    </div>
  );
}

// Export the canonical problem text so /projects/new can prefill from ?prefill=
export function getPrefillProblem(id) {
  return PROBLEM_FULL_TEXT[id] || null;
}

// ═════════════════════════════════════════════════════════════════════════════
// 5-Layer Venture Architecture — embedded at the top of Overview
// ═════════════════════════════════════════════════════════════════════════════
export function FiveLayerArchitecture({ doc }) {
  const KEY = "aignoz.fiveLayer.collapsed";
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(KEY) === "1");
  useEffect(() => { localStorage.setItem(KEY, collapsed ? "1" : "0"); }, [collapsed]);

  const fr = doc?.final_report || {};
  const rs = doc?.outputs?.research_scientist || {};
  const ia = doc?.outputs?.innovation_architect || {};
  const pt = doc?.outputs?.prototype_designer || {};
  const co = doc?.outputs?.commercialization_agent || {};

  const firstSentence = (s) => (s || "").split(/(?<=[.!?])\s+/)[0];
  const biology = [
    ...(rs.biomarkers || []).slice(0, 3),
    ...(rs.pathways || []).slice(0, 2),
  ].filter(Boolean);

  const layers = [
    { n: 1, name: "Biology",      icon: Dna,        tab: "innovation",  content: biology.length ? <ChipRow items={biology.map(b => typeof b === "string" ? b : (b.name || JSON.stringify(b).slice(0, 30)))} /> : <em className="text-white/40">No biomarker data</em> },
    { n: 2, name: "Technology",   icon: Cpu,        tab: "innovation",  content: <ChipRow items={fr.technology_stack || []} accent /> },
    { n: 3, name: "Intelligence", icon: Brain,      tab: "prototype",   content: <span>{ia.concepts?.[0]?.name || "—"}{pt.software_architecture ? <span className="text-white/60"> · {firstSentence(pt.software_architecture).slice(0, 130)}</span> : null}</span> },
    { n: 4, name: "Platform",     icon: Boxes,      tab: "prototype",   content: <span>{firstSentence(pt.system_architecture)?.slice(0, 180) || "—"}</span> },
    { n: 5, name: "Business",     icon: TrendingUp, tab: "commercial",  content: (
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-white/85">{firstSentence(co.business_model)?.slice(0, 140) || "—"}</span>
        <span className="flex items-center gap-1.5">
          {co.tam_usd != null && <ChipTiny label="TAM" value={formatUSD(co.tam_usd)} />}
          {co.sam_usd != null && <ChipTiny label="SAM" value={formatUSD(co.sam_usd)} />}
          {co.som_usd != null && <ChipTiny label="SOM" value={formatUSD(co.som_usd)} />}
        </span>
      </div>
    ) },
  ];

  return (
    <section data-testid="five-layer" className="mb-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">5-Layer Venture Architecture™</div>
          <div className="text-xs text-white/45">Every Aignoz venture engineered across all five layers.</div>
        </div>
        <button onClick={() => setCollapsed((c) => !c)} data-testid="five-layer-toggle"
          className="text-white/40 hover:text-white/80 transition text-[10px] inline-flex items-center gap-1 uppercase tracking-wider">
          {collapsed ? <>Expand <ChevronDown className="w-3 h-3" /></> : <>Collapse <ChevronUp className="w-3 h-3" /></>}
        </button>
      </div>
      {!collapsed && (
        <div className="space-y-1.5">
          {layers.map((L) => (
            <LayerRow key={L.n} L={L} runId={doc?.run_id} />
          ))}
        </div>
      )}
    </section>
  );
}

function LayerRow({ L, runId }) {
  return (
    <div className="glass rounded-lg border-white/[0.06] px-4 py-2.5 flex items-center gap-4" data-testid={`layer-${L.n}`}>
      <div className="shrink-0 w-9 h-9 rounded-md border border-teal-400/30 bg-teal-400/[0.06] flex items-center justify-center">
        <L.icon className="w-4 h-4 text-teal-300" />
      </div>
      <div className="shrink-0 w-32">
        <div className="text-[9px] mono text-teal-300/70">LAYER {L.n}</div>
        <div className="text-sm font-semibold tracking-tight">{L.name}</div>
      </div>
      <div className="flex-1 min-w-0 text-sm text-white/85 leading-snug truncate-2">{L.content}</div>
      {runId && (
        <Link to={`/projects/${runId}/${L.tab}`} className="shrink-0 text-[10px] text-teal-300/70 hover:text-teal-200 inline-flex items-center gap-1 uppercase tracking-wider whitespace-nowrap">
          View source <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}
function ChipRow({ items, accent = false }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.length === 0 && <em className="text-white/40">—</em>}
      {items.map((t, i) => (
        <span key={i} className={`text-[11px] px-1.5 py-0.5 rounded ${accent ? "bg-teal-400/12 border border-teal-400/25 text-teal-100" : "bg-white/[0.04] border border-white/[0.08] text-white/80"}`}>{t}</span>
      ))}
    </div>
  );
}
function ChipTiny({ label, value }) {
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.08] mono">
      <span className="text-white/45">{label}</span> <span className="text-teal-200">{value}</span>
    </span>
  );
}
